/**
 * notifications.js — push + local notification wiring.
 *
 * Push notifications are opt-in. The Capacitor plugin only asks the OS
 * for permission when `registerPush()` is called, so the customer app
 * can defer the prompt until a contextual moment (e.g. right after the
 * first successful charging session, when a "battery full" nudge is
 * demonstrably useful).
 *
 * Backend integration:
 *   `registerPush()` returns the device token. The current backend
 *   does NOT yet expose a device-registration endpoint — see the
 *   comment on `sendTokenToBackend` below. Until that endpoint exists,
 *   tokens are logged (fingerprint only) but never transmitted.
 */
import { isNative } from './platform.js';
import { requestNotificationPermission } from './permissions.js';

let _push = null;
async function pushPlugin() {
  if (_push) return _push;
  const mod = await import('@capacitor/push-notifications');
  _push = mod.PushNotifications;
  return _push;
}

let _local = null;
async function localPlugin() {
  if (_local) return _local;
  const mod = await import('@capacitor/local-notifications');
  _local = mod.LocalNotifications;
  return _local;
}

// ────────────────────────────────────────────────────────────────
// PUSH
// ────────────────────────────────────────────────────────────────

// Registered listener handles (native subscriptions). Owning them at
// module scope lets us tear them all down on logout without needing
// the caller to remember which listeners they installed.
const pushListenerHandles = [];

function shortFingerprint(token) {
  if (typeof token !== 'string' || !token) return '';
  return `${token.slice(0, 6)}…${token.slice(-4)}`;
}

/**
 * Backend contract stub.
 *
 * When the API grows a device-registration endpoint — e.g.
 * `POST /api/user/devices` with `{ token, platform, appVersion }` —
 * wire it in here. Until that day the function short-circuits so the
 * frontend integration is testable end-to-end without introducing a
 * fake production call.
 *
 * ⚠️ Do NOT log the raw token — only a fingerprint. The token is
 * effectively a device-identifying credential.
 */
async function sendTokenToBackend(_token, _extra) {
  // Intentionally not implemented. See the block-comment above.
  return { ok: false, reason: 'endpoint_not_implemented' };
}

/**
 * Request notification permission and register with APNs / FCM.
 * Resolves with `{ ok, state, token? }` where `state` is the resulting
 * permission state. Idempotent — safe to call on every app resume.
 *
 * @param {(payload: object) => void} [onNotification]
 *        Called when a push arrives while the app is in the foreground.
 * @param {(payload: object) => void} [onAction]
 *        Called when the user taps a notification (foreground OR
 *        background). Route users to the right screen from here.
 */
export async function registerPush({ onNotification, onAction } = {}) {
  if (!isNative()) return { ok: false, state: 'unavailable' };

  const perm = await requestNotificationPermission();
  if (perm.state !== 'granted') {
    return { ok: false, state: perm.state };
  }

  const push = await pushPlugin();

  // Tear down any listeners left over from a prior register — this
  // makes the call safe on hot reload / logout / re-login.
  await unregisterPushListeners();

  // The `registration` event fires with the device token; the token
  // shape is APNs on iOS, FCM on Android. Never log the raw token.
  let deviceToken = null;
  const readyPromise = new Promise((resolve) => {
    let settled = false;
    const settle = (payload) => {
      if (settled) return;
      settled = true;
      resolve(payload);
    };
    push.addListener('registration', (t) => {
      deviceToken = t?.value || null;
      settle({ ok: true, token: deviceToken });
    }).then((h) => pushListenerHandles.push(h)).catch(() => {});
    push.addListener('registrationError', (err) => {
      settle({ ok: false, error: err?.error || 'registration_error' });
    }).then((h) => pushListenerHandles.push(h)).catch(() => {});
    // Failsafe — if the OS never fires either event, don't hang the
    // caller forever. The permission was granted; the app is fine.
    setTimeout(() => settle({ ok: false, error: 'registration_timeout' }), 15000);
  });

  if (typeof onNotification === 'function') {
    push.addListener('pushNotificationReceived', onNotification)
      .then((h) => pushListenerHandles.push(h))
      .catch(() => {});
  }
  if (typeof onAction === 'function') {
    push.addListener('pushNotificationActionPerformed', onAction)
      .then((h) => pushListenerHandles.push(h))
      .catch(() => {});
  }

  try { await push.register(); } catch {
    return { ok: false, state: 'granted', error: 'register_failed' };
  }

  const result = await readyPromise;
  if (result.ok) {
    // Fingerprint-only logging — see block-comment on sendTokenToBackend.
    console.info('[push] registered', { platform: 'native', tokenFp: shortFingerprint(result.token) });
    // sendTokenToBackend is a stub — see block-comment above. When the
    // backend endpoint exists, replace with an awaited call here.
    sendTokenToBackend(result.token, {}).catch(() => {});
  }
  return { ok: result.ok, state: 'granted', token: result.token, error: result.error };
}

/** Remove all currently-registered push listeners. Best-effort. */
export async function unregisterPushListeners() {
  while (pushListenerHandles.length) {
    const h = pushListenerHandles.pop();
    try { await h.remove?.(); } catch { /* ignore */ }
  }
}

/**
 * De-register the device from push. Called at logout so the previous
 * user stops receiving notifications on this device. Best-effort —
 * failures are logged but do not block the caller.
 */
export async function deregisterPush() {
  if (!isNative()) return;
  try {
    const push = await pushPlugin();
    await unregisterPushListeners();
    // The Capacitor push plugin doesn't expose a native "unregister"
    // call on Android; on iOS it does. Both platforms clear when the
    // app is uninstalled — for our purposes, dropping the token on the
    // backend (when that endpoint exists) is the source of truth.
    if (typeof push.unregister === 'function') {
      await push.unregister();
    }
  } catch { /* ignore */ }
}

// ────────────────────────────────────────────────────────────────
// LOCAL
// ────────────────────────────────────────────────────────────────

/**
 * Schedule a local notification. Local notifications are used for
 * events whose timing the client already knows — for example, a nudge
 * five seconds after a charging session ends. Do NOT use them for
 * anything driven by uncertain client-side timing (a fake "battery
 * full in 20 minutes" notification would be misleading).
 *
 * Resolves with `{ ok, id }` on success, `{ ok: false, reason }` on
 * failure or when the plugin is unavailable.
 */
export async function scheduleLocalNotification({ id, title, body, atMs }) {
  if (!isNative()) return { ok: false, reason: 'unavailable' };
  const local = await localPlugin().catch(() => null);
  if (!local) return { ok: false, reason: 'unavailable' };

  try {
    const perm = await local.checkPermissions();
    if (perm?.display !== 'granted') {
      const req = await local.requestPermissions();
      if (req?.display !== 'granted') {
        return { ok: false, reason: 'permission_denied' };
      }
    }

    const numericId = typeof id === 'number' ? id : Math.floor(Math.random() * 2_000_000_000);
    const schedule = atMs && atMs > Date.now() ? { at: new Date(atMs) } : undefined;
    await local.schedule({
      notifications: [{
        id: numericId,
        title: title || 'GreenCharge',
        body: body || '',
        schedule,
      }],
    });
    return { ok: true, id: numericId };
  } catch (err) {
    return { ok: false, reason: 'error', message: err?.message };
  }
}

/** Cancel scheduled local notifications by id. Best-effort. */
export async function cancelLocalNotifications(ids) {
  if (!isNative() || !Array.isArray(ids) || ids.length === 0) return;
  const local = await localPlugin().catch(() => null);
  if (!local) return;
  try {
    await local.cancel({
      notifications: ids.map((id) => ({ id })),
    });
  } catch { /* ignore */ }
}
